import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faCheck } from "@fortawesome/free-solid-svg-icons";
import styled, { css } from "styled-components";
import { useBackgroundProjects } from "@hooks/useBackgroundData";
import { useProjectFilter } from "../../../../../context/ProjectFilterContext";

const FilterIconOverlay = ({ mode = "inline", placement = "default" }) => {
  const { t } = useTranslation();
  const { projects, isLoading } = useBackgroundProjects();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [show, setShow] = useState(false);
  const isRightPlacement = placement === "right";

  const projectList = (projects || [])
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));

  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId === selectedProjectId ? null : projectId);
    setShow(false);
  };

  const selectedProject =
    selectedProjectId === "without-project"
      ? { id: "without-project", name: t("without_project"), tree_count: 0 }
      : projects?.find((p) => p.id === selectedProjectId);

  return (
    <FilterOverlayWrapper $mode={mode}>
      <Dropdown show={show} drop="down" onToggle={(isOpen) => setShow(isOpen)}>
        <Dropdown.Toggle
          as={FilterButton}
          id="project-filter-dropdown"
          data-tour="project-filter"
          className={selectedProjectId ? "active" : ""}
        >
          {selectedProject && (
            <span className="selected-project-name" title={selectedProject.name}>
              {selectedProject.name}
            </span>
          )}
          <FontAwesomeIcon icon={faFilter} />
          {selectedProject && <span className="badge-count">1</span>}
        </Dropdown.Toggle>

        <StyledDropdownMenu
          $mode={mode}
          align={isRightPlacement ? "start" : "end"}
          popperConfig={
            isRightPlacement
              ? {
                  modifiers: [
                    {
                      name: "flip",
                      options: { fallbackPlacements: ["bottom-start", "bottom"] },
                    },
                    {
                      name: "preventOverflow",
                      options: { boundary: "viewport" },
                    },
                  ],
                }
              : undefined
          }
        >
          <DropdownHeader>
            <h6>{t("filter_by_project")}</h6>
            {selectedProjectId && (
              <ClearButton onClick={() => handleProjectClick(null)}>
                {t("clear_filter")}
              </ClearButton>
            )}
          </DropdownHeader>

          {isLoading ? (
            <div className="p-3 text-center text-muted">{t("loading")}...</div>
          ) : (
            <DropdownContent>
              {/* Without Project option */}
              <ProjectItem
                onClick={() => handleProjectClick("without-project")}
                className={selectedProjectId === "without-project" ? "active" : ""}
              >
                <div className="project-info">
                  <span className="project-name">{t("without_project")}</span>
                </div>
                {selectedProjectId === "without-project" && (
                  <FontAwesomeIcon icon={faCheck} className="check-icon" />
                )}
              </ProjectItem>

              {/* Divider */}
              {projectList.length > 0 && <MenuDivider />}

              {/* Regular projects */}
              {projectList.map((project) => {
                const isActive = selectedProjectId === project.id;
                return (
                  <ProjectItem
                    key={project.id}
                    onClick={() => handleProjectClick(project.id)}
                    className={isActive ? "active" : ""}
                  >
                    <div className="project-info">
                      <span className="project-name">{project.name}</span>
                      {project.tree_count > 0 && (
                        <span className="tree-count">
                          {project.tree_count} {t("trees_short")}
                        </span>
                      )}
                    </div>
                    {isActive && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                  </ProjectItem>
                );
              })}
            </DropdownContent>
          )}
        </StyledDropdownMenu>
      </Dropdown>
    </FilterOverlayWrapper>
  );
};

export default FilterIconOverlay;

// Styled Components
const FilterOverlayWrapper = styled.div`
  z-index: 1050;

  ${({ $mode }) =>
    $mode === "floating"
      ? css`
          position: fixed;
          top: 5rem;
          right: 1rem;

          @media (max-width: 768px) {
            top: 4.5rem;
            right: 0.5rem;
          }
        `
      : css`
          position: static;
          display: inline-flex;
        `}
`;

const FilterButton = styled.button`
  min-width: 52px;
  height: 48px;
  border-radius: 999px;
  background: white;
  border: 2px solid #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  &::after {
    display: none !important;
    content: none !important;
  }

  &:hover {
    background: #f8f9fa;
    border-color: var(--bs-primary, #0d6efd);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.active {
    background: var(--bs-primary, #0d6efd);
    border-color: var(--bs-primary, #0d6efd);
    color: white;

    &:hover {
      background: var(--bs-primary, #0d6efd);
    }
  }

  svg {
    font-size: 18px;
    flex-shrink: 0;
  }

  .selected-project-name {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
  }

  .badge-count {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #dc3545;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: bold;
    min-width: 18px;
    text-align: center;
  }

  @media (max-width: 768px) {
    min-width: 42px;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    padding: 0;
    gap: 0;

    svg {
      font-size: 16px;
    }

    .selected-project-name {
      display: none;
    }
  }

  [data-theme="dark"] & {
    background: var(--color-bg-surface, #0f1e2c);
    border-color: var(--color-border-default, #1e3448);
    color: var(--color-text-primary, #d4e4ee);
    box-shadow: var(--shadow-sm);
  }

  [data-theme="dark"] &:hover {
    background: var(--color-bg-raised, #162535);
    border-color: var(--color-border-strong, #2a4a62);
  }

  [data-theme="dark"] &.active {
    background: rgba(123, 175, 216, 0.18);
    border-color: rgba(123, 175, 216, 0.28);
    color: var(--color-text-primary, #d4e4ee);
  }
`;

const StyledDropdownMenu = styled(Dropdown.Menu)`
  min-width: 360px;
  max-width: 460px;
  overflow: visible;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 0;
  z-index: 1060;

  @media (max-width: 768px) {
    min-width: 280px;
    max-height: 60vh;

    ${({ $mode }) =>
      $mode === "inline" &&
      css`
        position: fixed !important;
        top: calc(4rem + 0.6rem) !important;
        left: 50% !important;
        right: auto !important;
        transform: translateX(-50%) !important;
        width: min(92vw, 340px);
        min-width: min(92vw, 340px);
        max-width: 92vw;
        margin: 0 !important;
      `}
  }

  [data-theme="dark"] & {
    background: var(--color-bg-surface, #0f1e2c);
    border-color: var(--color-border-subtle, #162535);
    box-shadow: var(--shadow-lg);
  }
`;

const DropdownHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;

  h6 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
  }

  [data-theme="dark"] & {
    background: var(--color-bg-raised, #162535);
    border-bottom-color: var(--color-border-subtle, #162535);
  }

  [data-theme="dark"] & h6 {
    color: var(--color-text-primary, #d4e4ee);
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: var(--bs-primary, #0d6efd);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #e7f1ff;
  }

  [data-theme="dark"] & {
    color: var(--color-interactive, #2a8fd5);
  }

  [data-theme="dark"] &:hover {
    background: rgba(42, 143, 213, 0.14);
  }
`;

const DropdownContent = styled.div`
  max-height: min(60vh, 28rem);
  overflow-y: auto;
  padding: 0.35rem;
  background: #fff;

  @media (max-width: 768px) {
    max-height: min(56vh, 24rem);
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  [data-theme="dark"] & {
    background: var(--color-bg-surface, #0f1e2c);
  }

  [data-theme="dark"] &::-webkit-scrollbar-track {
    background: var(--color-bg-surface, #0f1e2c);
  }

  [data-theme="dark"] &::-webkit-scrollbar-thumb {
    background: var(--color-border-strong, #2a4a62);
  }
`;

const ProjectItem = styled.div`
  padding: 0.8rem 0.9rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  transition: background 0.2s ease;
  background: transparent;
  border-radius: 0.75rem;

  &:hover {
    background: #f8f9fa;
  }

  &.active {
    background: #e7f1ff;

    .project-name {
      color: var(--bs-primary, #0d6efd);
      font-weight: 600;
    }
  }

  .project-info {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    flex: 1;
    min-width: 0;
  }

  .project-name {
    font-size: 14px;
    color: #212529;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
    line-height: 1.35;
  }

  .tree-count {
    font-size: 12px;
    color: #6c757d;
  }

  .check-icon {
    color: var(--bs-primary, #0d6efd);
    font-size: 14px;
    margin-top: 0.15rem;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    color: var(--color-text-primary, #d4e4ee);

    .project-name {
      color: var(--color-text-primary, #d4e4ee);
    }

    .tree-count {
      color: var(--color-text-secondary, #8ba3b5);
    }

    .check-icon {
      color: var(--color-text-primary, #d4e4ee);
    }
  }

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  [data-theme="dark"] &.active {
    background: rgba(123, 175, 216, 0.18);
  }
`;

const MenuDivider = styled.div`
  border-top: 1px solid #dee2e6;
  margin: 0.35rem 0;

  [data-theme="dark"] & {
    border-top-color: var(--color-border-subtle, #162535);
  }
`;
